import Vue from "vue";
import App from "./App.vue";
import router from "./router/router";
import store from "./store/store";
import Keycloak from "keycloak-js";
import vuetify from "./plugins/vuetify";
import i18n from "./i18n";
import axios from "axios";
import jwt_decode from "jwt-decode";
import VuetifyGoogleAutocomplete from "vuetify-google-autocomplete";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import "leaflet/dist/leaflet.css";
import vClickOutside from "v-click-outside";

import { commonModule, companyModule } from "@/store/modules/store-accessor";
import { consumePostLoginPath } from "@/helpers/portal-auth-redirect";
import { initClientContext } from "@/helpers/client-context";

// Device context for usage tracking. Started now so it overlaps login;
// the first API request waits briefly for version and OS.
initClientContext();

/** Old hash-mode URLs (/#/welcome) → history-mode paths (/welcome). */
function migrateLegacyHashRoute(): void {
  const { hash } = window.location;
  if (!hash.startsWith("#/")) return;
  const path = hash.slice(1);
  window.history.replaceState(null, "", path + window.location.search);
}
migrateLegacyHashRoute();

Vue.config.productionTip = false;
dayjs.extend(utc);
dayjs.extend(timezone);

Vue.prototype.$http = axios;
axios.defaults.headers.get["Accept"] = "application/json, application/pdf";

Vue.use(vClickOutside);

Vue.use(VuetifyGoogleAutocomplete, {
  apiKey:
    process.env.NODE_ENV === "production"
      ? "GOOGLE_MAPS_KEY"
      : "AIzaSyBgl51kld9IuDE2JcI8GYQUrq6kunvyV2A",
  language: localStorage.getItem("lang") || "el",
  installComponents: true,
  vueGoogleMapsCompatibility: false,
});

const initOptions: Record<string, string> = {
  url:
    process.env.NODE_ENV === "production"
      ? "SSO_URL"
      : "https://sso.domx-dev.com/auth",
  realm: "gastools",
  clientId: "gas-tools-frontend",
  onLoad: "check-sso",
};

const keycloak = new Keycloak(initOptions);
Vue.prototype.$keycloak = keycloak;

function mountApp(): void {
  new Vue({
    router,
    store,
    vuetify,
    i18n,
    render: (h) => h(App, { props: { keycloak } }),
  }).$mount("#app");

  if (keycloak.authenticated) {
    Vue.nextTick(() => {
      if (router.currentRoute.path === "/welcome") {
        return;
      }
      const pending = consumePostLoginPath();
      if (pending && router.currentRoute.fullPath !== pending) {
        router.replace(pending).catch(() => undefined);
      }
    });
  }
}

function bootstrapAuthenticatedSession(): void {
  mountApp();
  void companyModule.getCompanyInfo().catch(() => undefined);
  startTokenRefreshInterval();
}

function startTokenRefreshInterval(): void {
  setInterval(() => {
    keycloak
      .updateToken(240)
      .then((refreshed: boolean) => {
        if (refreshed && keycloak.token) {
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + keycloak.token;
        }
      })
      .catch(() => undefined);
  }, 60_000);
}

keycloak
  .init({
    onLoad: "check-sso",
    checkLoginIframe: false,
    silentCheckSsoRedirectUri: `${window.location.origin}${(
      process.env.BASE_URL || "/"
    ).replace(/\/?$/, "/")}silent-check-sso.html`,
  })
  .then((auth: boolean) => {
    (window as any).sharedKeycloak = keycloak;

    if (auth && keycloak.token) {
      const tokenClaims: Record<string, unknown> = jwt_decode(
        keycloak.token
      ) as Record<string, unknown>;
      if (!tokenClaims["portal_access"]) {
        keycloak.logout();
        return;
      }
      commonModule.setUserInfo(tokenClaims);
      commonModule.setControlAccess(tokenClaims["control_access"] as boolean);
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + keycloak.token;
      bootstrapAuthenticatedSession();
    } else {
      delete axios.defaults.headers.common["Authorization"];
      mountApp();
    }
  })
  .catch(() => {
    (window as any).sharedKeycloak = keycloak;
    delete axios.defaults.headers.common["Authorization"];
    mountApp();
  });
